import React, { useState, useEffect, useRef } from 'react';
import { TiArrowUnsorted } from "react-icons/ti";
import { TbPencil } from "react-icons/tb";
import { FaEdit, FaTrash } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import axios from 'axios';
import Select from 'react-select';
import { useCurrency } from '../../../../../context/currencyContext';
import Swal from 'sweetalert2'; // For beautiful alerts

// Custom Dropdown Component for Group selection
const CustomDropdown = ({ label, name, value, options, onChange, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (selectedValue) => {
    onChange({ target: { name, value: selectedValue } });
    setIsOpen(false);
  };

  const dropdownStyle = { position: 'relative', width: '1560px' };
  const dropdownHeaderStyle = {
    padding: '8px 10px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    fontSize: '14px',
    backgroundColor: '#f9f9f9',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: disabled ? '#999' : '#000',
  };
  const dropdownListStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '3px',
    marginTop: '2px',
    zIndex: 1000,
    maxHeight: '200px',
    overflowY: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  };

  return (
    <div style={dropdownStyle} ref={dropdownRef}>
      <div style={dropdownHeaderStyle} onClick={() => !disabled && setIsOpen(!isOpen)}>
        <span>{value || `Select ${label.toLowerCase()}`}</span>
        <span style={{ marginLeft: '10px', fontSize: '12px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <IoIosArrowDown />
        </span>
      </div>
      {isOpen && !disabled && (
        <div style={dropdownListStyle}>
          {options.map((option) => (
            <div
              key={option}
              style={{
                padding: '8px 10px',
                cursor: 'pointer',
                backgroundColor: '#fff'
              }}
              onClick={() => handleChange(option)}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#fff'}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Reusable simple input components
const SimpleNumberInput = ({ label, name, value, onChange, style, min, step = "any" }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
    <label style={{ fontWeight: 'bold', marginRight: '15px', color: '#333', width: '120px', flexShrink: '0' }}>{label}</label>
    <input type="number" name={name} value={value} onChange={onChange} style={style} min={min} step={step} />
  </div>
);

const SimpleTextInput = ({ label, name, value, onChange, style }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
    <label style={{ fontWeight: 'bold', marginRight: '15px', color: '#333', width: '120px', flexShrink: '0' }}>{label}</label>
    <input type="text" name={name} value={value} onChange={onChange} style={style} />
  </div>
);

const SimpleFormField = ({ label, name, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
    <label style={{ fontWeight: 'bold', marginRight: '15px', color: '#333', width: '120px', flexShrink: '0' }}>{label}</label>
    <div style={{ flexGrow: 1 }}>{children}</div>
  </div>
);

// Modal Form to Add/Edit Price
const AddPriceForm = ({ closeModal, onAddPrice, onEditPrice, editData, isEditing, editIndex }) => {
  const { formatAmount } = useCurrency();
  
  const [formData, setFormData] = useState(() => {
    if (isEditing && editData) {
      const pricingFor = editData.pricingFor || 'Group';
      return {
        price: editData.price || 0,
        pricingFor,
        group: pricingFor === 'Group' ? editData.target : 'default',
        newGroup: '',
        accountId: pricingFor === 'Single Account' ? editData.target : '',
        accountName: pricingFor === 'Single Account' ? editData.accountName : '',
        extCost: editData.externalData?.extCost || 0,
        extPID: editData.externalData?.extPID || '',
        extVPID: editData.externalData?.extVPID || '',
        extDesc: editData.externalData?.extDesc || '',
      };
    }
    return {
      price: 0,
      pricingFor: 'Group',
      group: 'default',
      newGroup: '',
      accountId: '',
      accountName: '',
      extCost: 0,
      extPID: '',
      extVPID: '',
      extDesc: '',
    };
  });

  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const procedureId = sessionStorage.getItem('procedureId');

  // Get userLabId from localStorage (tries multiple possible keys)
  const getUserLabId = () => {
    return localStorage.getItem('userLabId') ||
           localStorage.getItem('labId') ||
           localStorage.getItem('labID') ||
           localStorage.getItem('userId') ||
           localStorage.getItem('userID');
  };

  // Fetch accounts and groups on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const userLabId = getUserLabId();
      
      if (!userLabId) {
        setError("User Lab ID not found. Please login again.");
        setLoading(false);
        return;
      }

      try {
        const accountsResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`);
        setAccounts(accountsResponse.data.data || []);
        
        const groupsResponse = await axios.get('https://hms.backend.hiragroupcloud.com/api/prices/get');
        setGroups(groupsResponse.data.map(g => g.name) || []);
      } catch (err) {
        setError('Failed to load accounts or groups.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle account selection from react-select
  const handleAccountChange = (selectedOption) => {
    const accountId = selectedOption ? selectedOption.value : '';
    const accountName = selectedOption ? selectedOption.label : '';
    setFormData((prev) => ({ ...prev, accountId, accountName }));
  };

  // Submit form - add or edit price
  const handleSubmit = async () => {
    const userLabId = getUserLabId();
    
    if (!userLabId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User Lab ID not found. Please login again.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    if (!procedureId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Procedure ID is missing. Please select a procedure first.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    if (!formData.price || formData.price <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Price',
        text: 'Price is required and must be greater than 0.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    if (!formData.pricingFor) {
      Swal.fire({
        icon: 'warning',
        title: 'Required',
        text: 'Please select Pricing For.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    let effectiveTarget = '';
    let effectiveAccountName = '';

    if (formData.pricingFor === 'Group') {
      effectiveTarget = formData.group === 'New Group' ? formData.newGroup : formData.group;
      if (!effectiveTarget) {
        Swal.fire({
          icon: 'warning',
          title: 'Required',
          text: 'Please select or create a group.',
          confirmButtonColor: '#004aad'
        });
        return;
      }

      // Create new group if needed
      if (formData.group === 'New Group') {
        const groupName = formData.newGroup.trim();
        if (!groupName) {
          Swal.fire({
            icon: 'warning',
            title: 'Required',
            text: 'New group name is required.',
            confirmButtonColor: '#004aad'
          });
          return;
        }

        try {
          await axios.post('https://hms.backend.hiragroupcloud.com/api/prices/add', { name: groupName, userLabId });
          const groupsResponse = await axios.get('https://hms.backend.hiragroupcloud.com/api/prices/get');
          setGroups(groupsResponse.data.map(g => g.name) || []);
          setFormData(prev => ({ ...prev, group: groupName }));
        } catch (err) {
          if (err.response?.status === 400 && err.response?.data?.message === 'Group already exists') {
            // Continue if group already exists
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: 'Failed to create new group: ' + (err.response?.data?.message || err.message),
              confirmButtonColor: '#004aad'
            });
            return;
          }
        }
      }
    } else if (formData.pricingFor === 'Single Account') {
      effectiveTarget = formData.accountId;
      effectiveAccountName = formData.accountName;
      if (!effectiveTarget) {
        Swal.fire({
          icon: 'warning',
          title: 'Required',
          text: 'Account selection is required for Single Account pricing.',
          confirmButtonColor: '#004aad'
        });
        return;
      }
    }

    const pricePayload = {
      procedureId,
      price: parseFloat(formData.price) || 0,
      pricingFor: formData.pricingFor,
      target: formData.pricingFor === 'Default' ? undefined : effectiveTarget,
      ...(formData.pricingFor === 'Single Account' && { accountName: effectiveAccountName }),
      externalData: {
        extCost: parseFloat(formData.extCost) || 0,
        extPID: formData.extPID || '',
        extVPID: formData.extVPID || '',
        extDesc: formData.extDesc || '',
      },
      userLabId,
    };

    try {
      if (isEditing) {
        const id = editData._id;
        const response = await axios.put(`https://hms.backend.hiragroupcloud.com/api/prices/edit/${id}`, pricePayload);
        onEditPrice(response.data, editIndex);
      } else {
        const response = await axios.post('https://hms.backend.hiragroupcloud.com/api/prices', pricePayload);
        onAddPrice(response.data);
      }
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Failed',
        text: 'Failed to save price: ' + (error.response?.data?.message || error.message),
        confirmButtonColor: '#004aad'
      });
    }
  };

  const filteredGroups = groups.filter(g => g !== 'default');
  const groupOptions = ['default', 'New Group', ...filteredGroups];

  return (
    <div style={{ backgroundColor: '#fff', width: '600px', maxWidth: '90%', margin: '40px auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '4px', overflow: 'hidden', fontFamily: 'Arial, sans-serif', fontSize: '14px', border: '1px solid #ccc', maxHeight: '80vh' }}>
      <div style={{ backgroundColor: '#004aad', color: '#fff', padding: '10px 15px', fontSize: '16px', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{isEditing ? 'Edit Price' : 'Add Price'}</span>
        <span style={{ cursor: 'pointer', fontSize: '18px' }} onClick={closeModal}>&times;</span>
      </div>
      <div style={{ padding: '15px', overflowY: 'auto', maxHeight: 'calc(80vh - 110px)' }}>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        {loading && <p>Loading...</p>}
        
        <div style={{ color: '#333', fontSize: '16px', fontWeight: 'bold', padding: '15px 0 5px 0', margin: '10px 0', borderBottom: '2px solid #ccc' }}>General</div>
        
        <SimpleNumberInput
          label={`Price (${formatAmount(0).replace('0.00', '')})`}
          name="price"
          value={formData.price}
          onChange={handleInputChange}
          style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100px' }}
          min="0"
          step="0.01"
        />
        
        <SimpleFormField label="Pricing For">
          <select name="pricingFor" value={formData.pricingFor} onChange={handleInputChange} style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100%' }}>
            <option value="Group">Group</option>
            <option value="Single Account">Single Account</option>
            <option value="Default">Default</option>
          </select>
        </SimpleFormField>

        {formData.pricingFor === 'Group' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
              <label style={{ fontWeight: 'bold', marginRight: '15px', color: '#333', width: '120px', flexShrink: '0' }}>Group</label>
              <div style={{ flexGrow: 1 }}>
                <CustomDropdown
                  label="Group"
                  name="group"
                  value={formData.group}
                  options={groupOptions}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </div>
            </div>
            {formData.group === 'New Group' && (
              <SimpleTextInput label="New Group" name="newGroup" value={formData.newGroup} onChange={handleInputChange} style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100%' }} />
            )}
          </>
        )}

        {formData.pricingFor === 'Single Account' && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', padding: '0 10px' }}>
            <label style={{ fontWeight: 'bold', marginRight: '15px', color: '#333', width: '120px', flexShrink: '0' }}>Account</label>
            <div style={{ flexGrow: 1 }}>
              <Select
                options={accounts.map(a => ({ value: a.accountId, label: a.account_name }))}
                value={accounts.find(a => a.accountId === formData.accountId) ? { value: formData.accountId, label: formData.accountName } : null}
                onChange={handleAccountChange}
                isDisabled={loading}
                placeholder="Select Account"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    padding: '2px 0',
                    border: '1px solid #ccc',
                    borderRadius: '3px',
                    fontSize: '14px',
                    minHeight: '34px'
                  })
                }}
              />
            </div>
          </div>
        )}

        <div style={{ color: '#333', fontSize: '16px', fontWeight: 'bold', padding: '15px 0 5px 0', margin: '10px 0', borderBottom: '2px solid #ccc' }}>External data</div>
        
        <SimpleNumberInput label={`Ext. Cost (${formatAmount(0).replace('0.00', '')})`} name="extCost" value={formData.extCost} onChange={handleInputChange} style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100px' }} min="0" step="0.01" />
        <SimpleTextInput label="Ext. PID" name="extPID" value={formData.extPID} onChange={handleInputChange} style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100%' }} />
        <SimpleTextInput label="Ext. VPID" name="extVPID" value={formData.extVPID} onChange={handleInputChange} style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100%' }} />
        <SimpleTextInput label="Ext. Desc." name="extDesc" value={formData.extDesc} onChange={handleInputChange} style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: '3px', fontSize: '14px', width: '100%' }} />
      </div>
      
      <div style={{ padding: '15px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #eee', backgroundColor: '#f9f9f9' }}>
        <button style={{ padding: '8px 15px', marginRight: '10px', border: '1px solid #004aad', borderRadius: '3px', cursor: 'pointer', backgroundColor: '#e6f0fa', color: '#333', fontWeight: 'bold' }} onClick={closeModal}>
          Cancel
        </button>
        <button style={{ padding: '8px 15px', border: 'none', borderRadius: '3px', cursor: 'pointer', backgroundColor: '#004aad', color: '#fff', fontWeight: 'bold' }} onClick={handleSubmit}>
          {isEditing ? 'Update' : 'Add'}
        </button>
      </div>
    </div>
  );
};

// Main Pricing Component
const Pricing = () => {
  const { formatAmount } = useCurrency();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [accountsMap, setAccountsMap] = useState(new Map());
  const [isEditing, setIsEditing] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const procedureId = sessionStorage.getItem('procedureId');

  // Get userLabId from localStorage
  const getUserLabId = () => {
    return localStorage.getItem('userLabId') ||
           localStorage.getItem('labId') ||
           localStorage.getItem('labID') ||
           localStorage.getItem('userId') ||
           localStorage.getItem('userID');
  };

  // Fetch prices and accounts when procedureId changes
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      const userLabId = getUserLabId();
      
      if (!userLabId) {
        setError("User Lab ID not found. Please login again.");
        setLoading(false);
        return;
      }

      if (!procedureId) {
        setError('No Procedure ID found. Please select a procedure to edit.');
        setLoading(false);
        return;
      }
      
      try {
        const pricesResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/prices/procedure/${procedureId}`);
        setTableData(pricesResponse.data || []);
        
        const accountsResponse = await axios.get(`https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${userLabId}`);
        const accList = accountsResponse.data.data || [];
        const map = new Map(accList.map(a => [a.accountId, a.account_name]));
        setAccountsMap(map);
      } catch (err) {
        setError('Failed to load prices or accounts.');
      } finally {
        setLoading(false);
      }
    };
    
    if (procedureId) fetchData();
  }, [procedureId]);

  const openModal = () => {
    const userLabId = getUserLabId();
    if (!procedureId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No Procedure ID found. Please select a procedure first.',
        confirmButtonColor: '#004aad'
      });
      return;
    }
    if (!userLabId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User Lab ID not found. Please login again.',
        confirmButtonColor: '#004aad'
      });
      return;
    }
    setIsEditing(false);
    setEditData(null);
    setEditIndex(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index) => {
    setIsEditing(true);
    setEditIndex(index);
    setEditData(tableData[index]);
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  const handleAddPrice = (newPrice) => {
    setTableData((prev) => [...prev, newPrice]);
  };

  const handleEditPrice = (updatedPrice, index) => {
    setTableData((prev) => prev.map((row, i) => (i === index ? updatedPrice : row)));
  };

  // Delete price with confirmation
  const handleDeletePrice = async (index) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this price? This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    const userLabId = getUserLabId();
    const id = tableData[index]._id;

    try {
      await axios.delete(`https://hms.backend.hiragroupcloud.com/api/prices/${id}`, { 
        data: { procedureId, userLabId } 
      });
      setTableData((prev) => prev.filter((_, i) => i !== index));
      
      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Price deleted successfully.',
        confirmButtonColor: '#004aad',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete price.',
        confirmButtonColor: '#004aad'
      });
    }
  };

  // Save button (placeholder - shows success message)
  const handleSave = async () => {
    Swal.fire({
      icon: 'success',
      title: 'Success!',
      text: 'Pricing data saved successfully!',
      confirmButtonColor: '#004aad'
    });
  };

  return (
    <>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>Loading...</p>}
      
      {/* Table Header */}
      <div style={{ display: 'flex', backgroundColor: '#f4f4f4', padding: '10px', justifyContent: 'space-between' }}>
        <div style={{ flex: 0.5, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Price ({formatAmount(0).replace('0.00', '')}) <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Group/Account <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Pricing For <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Attributes <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Ext. Cost ({formatAmount(0).replace('0.00', '')}) <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          External Data <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          Added By <TiArrowUnsorted style={{ marginLeft: '5px', color: '#666', fontSize: '12px' }} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>Actions</div>
      </div>
      
      {/* Table Rows */}
      {tableData.map((row, index) => (
        <div key={row._id || index} style={{ display: 'flex', padding: '10px', justifyContent: 'space-between', borderBottom: '1px solid #ddd', backgroundColor: '#fff' }}>
          <div style={{ flex: 0.5, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {formatAmount(row.price)}
          </div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {row.pricingFor === 'Default' ? 'Default' :
             row.pricingFor === 'Group' ? row.target :
             (row.accountName || accountsMap.get(row.target) || row.target)}
          </div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{row.pricingFor}</div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{row.attributes || 'None'}</div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {formatAmount(row.externalData?.extCost || 0)}
          </div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {`${row.externalData?.extPID || ''}, ${row.externalData?.extVPID || ''}, ${row.externalData?.extDesc || ''}`}
          </div>
          <div style={{ flex: 1, textAlign: 'center', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {row.addedBy || 'N/A'}
          </div>
          <div style={{ flex: 1, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaEdit style={{ margin: '0 10px', cursor: 'pointer', fontSize: '16px', color: '#333' }} onClick={() => openEditModal(index)} title="Edit" />
            <FaTrash style={{ margin: '0 10px', cursor: 'pointer', fontSize: '16px', color: '#333' }} onClick={() => handleDeletePrice(index)} title="Delete" />
          </div>
        </div>
      ))}
      
      <button style={{ backgroundColor: '#ddd', color: '#333', border: 'none', padding: '10px 20px', cursor: 'pointer', fontSize: '16px', marginTop: '10px', width: '100px', borderRadius: '4px', marginBottom: '10px' }} onClick={openModal}>
        +
      </button>
      
      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '10px', marginBottom: '15px' }}>
        * You will need to click the save button below to actually save the pricing.
      </div>
      
      {/* Placeholder sections */}
      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginTop: '20px', marginBottom: '5px', borderBottom: '2px solid #ccc', paddingBottom: '0' }}>Invoice Details</div>
      <div style={{ backgroundColor: '#f4f4f4', padding: '10px', marginBottom: '10px' }}>
        <label><input type="checkbox" /> Roll-up Attribute Prices into Procedure on Invoice</label>
      </div>
      
      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginTop: '20px', marginBottom: '5px', borderBottom: '2px solid #ccc', paddingBottom: '0' }}>Tax Details</div>
      <div style={{ backgroundColor: '#f4f4f4', padding: '10px', marginBottom: '10px' }}>Taxable?</div>
      
      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginTop: '20px', marginBottom: '5px', borderBottom: '2px solid #ccc', paddingBottom: '0' }}>Revenue Centers Details</div>
      <div style={{ backgroundColor: '#f4f4f4', padding: '10px', marginBottom: '10px' }}>
        Revenue Centers &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; 
        <span style={{ color: '#333' }}>Select Revenue Centers</span>
      </div>
      
      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginTop: '20px', marginBottom: '5px' }}>Attribute Prices</div>
      <div style={{ backgroundColor: '#fff', padding: '10px', marginBottom: '10px' }}>
        <div style={{ marginLeft: '10px' }}>No attribute prices</div>
      </div>
      
      <button style={{ backgroundColor: '#ddd', color: '#333', border: 'none', padding: '10px 20px', cursor: 'pointer', fontSize: '16px', width: '100px', borderRadius: '4px', margin: '20px auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={handleSave}>
        <TbPencil style={{ marginRight: '5px', fontSize: '24px', color: '#333' }} /> Save
      </button>
      
      {/* Modal Overlay */}
      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <AddPriceForm 
            closeModal={closeModal} 
            onAddPrice={handleAddPrice} 
            onEditPrice={handleEditPrice} 
            editData={editData} 
            isEditing={isEditing} 
            editIndex={editIndex} 
          />
        </div>
      )}
    </>
  );
};

export default Pricing;